import React, { useCallback, useRef, memo } from 'react';
import { identity } from 'ramda';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { I18n } from 'js/components/common/I18n';
import { clsx, clsb } from 'js/utils/classNames.util';
import { componentService } from 'js/services/component.service';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from '../../helpers';
import { withFinalFormField } from '../../hocs';
import { FieldBottomMessage } from '../FieldBottomMessage';

import '../../FormFields.scss';
import style from './Checkbox.scss';

export const CheckboxBare: React.FC<Types.ICheckboxProps> = ({
    id,
    meta,
    label,
    onBlur,
    checked,
    onFocus,
    onChange,
    isToggle,
    onKeyDown,
    classNames,
    infoTooltip,
    className = '',
    disabled = false,
    readOnly = false,
    isLabelAsteriskEnabled,
    ...props
}) => {
    const idRef = useRef<string>(id || uuid());
    const translate = useTranslations('playtech.mobile.system.checkbox.');
    const checkboxRef = useRef<HTMLInputElement>(null);
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const titleAriaLabel = translate('aria-label.title');
    const stateAriaLabel = checked
        ? translate('aria-label.checked')
        : translate('aria-label.unchecked');

    const labelContent = typeof label === 'string'
        ? <I18n tKey={label} defaultValue={label} />
        : label;

    const interactionClass = helpers.getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>): void => {
            const { key } = event;

            onKeyDown?.(event);

            if (key === ' ' || key === 'Spacebar') {
                event.preventDefault();

                checkboxRef.current?.click();
            }
        },
        [checkboxRef, onKeyDown]
    );

    return (
        <div className={clsx([clsb([style, classNames], ['container']), interactionClass, checked ? 'checked' : ''])}>
            <AccessibilityElement
                tagName="label"
                htmlFor={idRef.current}
                onKeyDown={handleOnKeyDown}
                tabIndex={isDisabled || readOnly ? -1 : 0}
                aria-label={`${titleAriaLabel} ${props['aria-label'] || ''} ${stateAriaLabel}`}
                className={clsx([clsb([style, classNames], ['containerInner']), className])}
            >
                <input
                    tabIndex={-1}
                    type='checkbox'
                    ref={checkboxRef}
                    id={idRef.current}
                    readOnly={readOnly}
                    disabled={isDisabled}
                    className={clsb([style, classNames], ['input'])}
                    {...props}
                    // we need to pass checked prop directly otherwise
                    // checkbox will change its state from checked true to false only starting from second click
                    {...checked === undefined ? {} : { checked }}
                    onChange={readOnly ? identity : onChange}
                    onBlur={readOnly ? identity : onBlur}
                    onFocus={readOnly ? identity : onFocus}
                />

                { isToggle && <div className={clsb([style, classNames], ['toggle'])} /> }

                {!isToggle && (
                    <>
                        <Icon disabled={isDisabled || readOnly} type="checkboxChecked" className={clsx([clsb([style, classNames], ['checkbox']), style.checkboxChecked])} />
                        <Icon disabled={isDisabled || readOnly} type="checkboxUnchecked" className={clsx([clsb([style, classNames], ['checkbox']), style.checkboxUnchecked])} />
                    </>
                )}

                {label && (
                    <Label
                        id={idRef.current}
                        label={labelContent}
                        classNames={classNames}
                        infoTooltip={infoTooltip}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        className={clsx([clsb([style, classNames], ['label'])])}
                    />
                )}
            </AccessibilityElement>

            <FieldBottomMessage meta={meta} classNames={classNames} />
        </div>
    );
};

export const CheckboxPure = memo(CheckboxBare);
export const Checkbox = withFinalFormField<string>({ type: 'checkbox' })(CheckboxPure);

componentService.registerWebContentComponents({ Checkbox: CheckboxPure });
